import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Alert,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import type { GridProps } from '@mui/material/Grid';
import {
  Visibility,
  Assessment,
  TrendingUp,
  TrendingDown,
  Person,
  MonetizationOn,
  SportsEsports,
  Group,
  AttachMoney,
  DateRange,
  RestartAlt
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';

interface UserSettlementSummary {
  userId: number;
  username: string;
  fullName: string;
  ownBetsCount: number;
  ownBetsAmount: number;
  ownWins: number;
  ownLosses: number;
  downlineBetsCount: number;
  downlineBetsAmount: number;
  salesCommission: number;
  hierarchicalEarned: number;
  hierarchicalOwed: number;
  netPosition: number;
}

interface SettlementSummary {
  users: UserSettlementSummary[];
  reportDate: string;
  totalBetsProcessed: number;
  totalUsersAffected: number;
  totalSalesVolume: number;
  totalWinAmount: number;
}

interface UserDetailReport {
  userId: number;
  username: string;
  fullName: string;
  reportDate: string;
  ownBetsCount: number;
  ownBetsAmount: number;
  ownWins: number;
  ownLosses: number;
  ownWinAmount: number;
  ownBets: BetDetail[];
  downlineDetails: DownlineDetail[];
  totalSalesCommission: number;
  totalHierarchicalEarned: number;
  totalHierarchicalOwed: number;
  netPosition: number;
}

interface DownlineDetail {
  downlineUserId: number;
  downlineUsername: string;
  downlineFullName: string;
  bets: BetDetail[];
  salesCommissionFromDownline: number;
  hierarchicalEarnedFromDownline: number;
  hierarchicalOwedFromDownline: number;
  netFromDownline: number;
}

interface BetDetail {
  betId: number;
  betNumber: string;
  betAmount: number;
  operatorName: string;
  gameType: string;
  isWin: boolean;
  winningTier: string;
  winAmount: number;
  placedAt: string;
  userTakePercentage: number;
  salesCommissionRate: number;
  salesCommissionAmount: number;
  hierarchicalAmount: number;
}

const SettlementSummaryTable: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<SettlementSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [resetLoading, setResetLoading] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      // Format date as YYYY-MM-DD in local timezone to avoid timezone issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.get(`/settlement/report/${dateStr}`);
      setSummary(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch settlement summary');
    } finally {
      setLoading(false);
    }
  };

  const handleViewUserDetail = (userId: number) => {
    // Format date as YYYY-MM-DD in local timezone to avoid timezone issues
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Navigate to the new page with userId and date as query parameter
    navigate(`/settlement/user/${userId}?date=${dateStr}`);
  };

  const processSettlement = async () => {
    setLoading(true);
    setError(null);
    try {
      // Format date as YYYY-MM-DD in local timezone to avoid timezone issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.post('/settlement/process', {
        settlementDate: dateStr
      });
      
      if (response.data.success) {
        await fetchSummary(); // Refresh the summary
        alert(`Settlement processed successfully!\n${response.data.message}`);
      } else {
        setError(response.data.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to process settlement');
    } finally {
      setLoading(false);
    }
  };

  const resetSettlement = async () => {
    if (!window.confirm(`Are you sure you want to reset all settlement data for ${selectedDate.toLocaleDateString()}? This action cannot be undone.`)) {
      return;
    }

    setResetLoading(true);
    setError(null);
    try {
      // Format date as YYYY-MM-DD in local timezone to avoid timezone issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.post(`/settlement/reset/${dateStr}`);
      
      if (response.data.success) {
        setError(null);
        alert(`Reset successful! ${response.data.message}`);
        // Clear the current summary since data has been reset
        setSummary(null);
      } else {
        setError(response.data.message || 'Reset failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset settlement');
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [selectedDate]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };

  const getNetPositionColor = (netPosition: number) => {
    if (netPosition > 0) return 'success';
    if (netPosition < 0) return 'error';
    return 'default';
  };

  const getNetPositionIcon = (netPosition: number) => {
    if (netPosition > 0) return <TrendingUp />;
    if (netPosition < 0) return <TrendingDown />;
    return <AttachMoney />;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Settlement & Reports
        </Typography>

        {/* Controls */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 2.4 }}>
                <DatePicker
                  label="Report Date"
                  value={selectedDate}
                  onChange={(newValue) => newValue && setSelectedDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2.4 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={fetchSummary}
                  disabled={loading}
                  startIcon={<Assessment />}
                  fullWidth
                >
                  Load Report
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 2.4 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={processSettlement}
                  disabled={loading}
                  startIcon={<DateRange />}
                  fullWidth
                >
                  Process Settlement
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 2.4 }}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={resetSettlement}
                  disabled={resetLoading || loading}
                  startIcon={<RestartAlt />}
                  fullWidth
                >
                  {resetLoading ? 'Resetting...' : 'Reset Day'}
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 2.4 }}>
                {(loading || resetLoading) && <CircularProgress size={24} />}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Summary Stats */}
        {summary && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Settlement Summary for {new Date(summary.reportDate).toLocaleDateString()}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {summary.totalBetsProcessed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Bets Processed
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {summary.totalUsersAffected}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Users Affected
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {formatCurrency(summary.totalSalesVolume)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sales Volume
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="warning.main">
                      {formatCurrency(summary.totalWinAmount)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Wins
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Users Table */}
        {summary && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell align="center">
                    <Tooltip title="User's own betting activity">
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <Person sx={{ mr: 1 }} />
                        Own Bets
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="User's own wins and losses">
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <SportsEsports sx={{ mr: 1 }} />
                        Own Results
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Bets from all downlines">
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <Group sx={{ mr: 1 }} />
                        Downline Bets
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Commission from all bets">
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <MonetizationOn sx={{ mr: 1 }} />
                        Sales Commission
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Earned from downline losses">
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <TrendingUp sx={{ mr: 1 }} />
                        Hierarchical Earned
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Owed from downline wins">
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <TrendingDown sx={{ mr: 1 }} />
                        Hierarchical Owed
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Net position (earned - owed)">
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <Assessment sx={{ mr: 1 }} />
                        Net Position
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.users.map((user) => (
                  <TableRow key={user.userId} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {user.fullName || user.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          @{user.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {user.ownBetsCount} bets
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(user.ownBetsAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <Chip
                          label={`${user.ownWins}W`}
                          size="small"
                          color="success"
                          sx={{ mb: 0.5 }}
                        />
                        <Chip
                          label={`${user.ownLosses}L`}
                          size="small"
                          color="error"
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {user.downlineBetsCount} bets
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(user.downlineBetsAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="success.main" fontWeight="medium">
                        {formatCurrency(user.salesCommission)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="success.main" fontWeight="medium">
                        {formatCurrency(user.hierarchicalEarned)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="error.main" fontWeight="medium">
                        {formatCurrency(user.hierarchicalOwed)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={getNetPositionIcon(user.netPosition)}
                        label={formatCurrency(Math.abs(user.netPosition))}
                        color={getNetPositionColor(user.netPosition)}
                        variant={user.netPosition !== 0 ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View detailed breakdown">
                        <IconButton
                          size="small"
                          onClick={() => handleViewUserDetail(user.userId)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      </Box>
    </LocalizationProvider>
  );
};

export default SettlementSummaryTable;
